import React, { useState } from 'react';
import api from '../../services/api';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';


function MapaTrenInner({ paradas }) {
  const map = useMap();

  React.useEffect(() => {
    if (!paradas || paradas.length === 0) return;
    const layers = [];
    const BASE_TRAMOS = "https://services7.arcgis.com/XTupIrLX53AjaJqO/arcgis/rest/services/Red_Ferroviaria_de_Inter%C3%A9s_General___Adif__RFIG_CM_vista_2/FeatureServer/43/query";
    const BASE_DEPS = "https://services7.arcgis.com/XTupIrLX53AjaJqO/arcgis/rest/services/Dependencias_Adif_p%C3%BAblico/FeatureServer/0/query";

    const cargarYDibujar = async () => {
      // Buscar coordenadas y COD_TRAMO de cada estación en ADIF
      const estacionesInfo = [];
      const tramosUnicos = new Set();

      for (const p of paradas) {
        const codEstacion = p.estacion.split('-')[0].trim();
        try {
          const r = await fetch(`${BASE_DEPS}?f=geojson&where=COD_DEPEND='${codEstacion}'&outFields=COD_DEPEND,NOMBRE,COD_TRAMO&resultRecordCount=1`);
          const data = await r.json();
          if (data.features && data.features.length > 0) {
            const feat = data.features[0];
            const coords = feat.geometry?.coordinates;
            const tramo = feat.properties?.COD_TRAMO;
            if (coords && tramo) {
              estacionesInfo.push({ ...p, lat: coords[1], lon: coords[0], codTramo: tramo });
              tramosUnicos.add(tramo);
            }
          }
        } catch {}
      }

      if (estacionesInfo.length === 0) return;

      // Red AV de fondo en gris
      const filtroAV = "CODTRAMO LIKE '11%' OR CODTRAMO LIKE '12%' OR CODTRAMO LIKE '13%' OR CODTRAMO LIKE '14%'";
      fetch(`${BASE_TRAMOS}?f=geojson&where=${encodeURIComponent(filtroAV)}&outFields=CODTRAMO&resultRecordCount=2000`)
        .then(r => r.json())
        .then(data => {
          if (data.features) {
            const layer = L.geoJSON(data, { style: { color: '#cbd5e1', weight: 1.5, opacity: 0.4 } }).addTo(map);
            layers.push(layer);
          }
        }).catch(() => {});

      // Unir estaciones en orden con línea simple
      const puntos = estacionesInfo.map(e => [e.lat, e.lon]);
      if (puntos.length > 1) {
        const linea = L.polyline(puntos, { color: '#2563eb', weight: 4, opacity: 0.9 }).addTo(map);
        layers.push(linea);
        map.fitBounds(linea.getBounds(), { padding: [40, 40] });
      }

      // Marcadores de paradas
      estacionesInfo.forEach((c, i) => {
        const color = i === 0 ? '#16a34a' : i === estacionesInfo.length - 1 ? '#dc2626' : '#2563eb';
        const marker = L.circleMarker([c.lat, c.lon], {
          radius: 8, fillColor: color, color: 'white', weight: 2, fillOpacity: 1
        }).addTo(map);
        const retraso = parseInt(c.retraso_salida || c.retraso_llegada || '0');
        const retrasoLabel = retraso < 0 ? `${Math.abs(retraso)} min antes` : retraso === 0 ? 'Puntual' : `+${retraso} min`;
        marker.bindPopup(`<strong>${c.estacion}</strong><br>${retrasoLabel}`);
        layers.push(marker);
      });
    };

    cargarYDibujar();
    return () => layers.forEach(l => { try { map.removeLayer(l); } catch {} });
  }, [map, paradas]);

  return null;
}

function MapaTren({ paradas }) {
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e5e5', height: 400, marginTop: 12 }}>
      <MapContainer center={[40.4, -3.7]} zoom={6} style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
        <MapaTrenInner paradas={paradas} />
      </MapContainer>
    </div>
  );
}

export default function ConsultaTren() {
  const [tren, setTren] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const consultar = async () => {
    if (!tren) return;
    setLoading(true);
    setError(null);
    setResultado(null);
    // Limpiar resultado anterior inmediatamente
    try {
      const r = await api.get(`/siges/tren/${tren}?fecha=${fecha}`);
      setResultado(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al consultar SIGES — verifica que el proxy está activo');
    } finally {
      setLoading(false);
    }
  };

  const formatHora = (h) => {
    if (!h) return '—';
    const s = h.replace('.0', '').padStart(4, '0');
    return `${s.slice(0,2)}:${s.slice(2)}`;
  };

  const colorRetraso = (r) => {
    if (!r && r !== '0') return '#888';
    const n = parseInt(r);
    if (n <= 2 && n >= -2) return '#16a34a';
    if (n > 2 && n <= 60) return '#854f0b';
    if (n > 60) return '#a32d2d';
    return '#16a34a';
  };

  const labelRetraso = (r, haPasado) => {
    if (!haPasado) return null;
    if (r === null || r === undefined || r === '') return 'Puntual';
    const n = parseInt(r);
    if (n === 0) return 'Puntual';
    if (n < 0 && n >= -2) return `${Math.abs(n)} min adelanto`;
    if (n < -2) return `${Math.abs(n)} min adelanto`;
    if (n <= 60) return `${n} min retraso`;
    return `+${n} min retraso`;
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Consulta de tren</h2>
        <p style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Horarios reales desde SIGES — ADIF</p>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e5e5', padding: 20, marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Número de tren</label>
          <input value={tren} onChange={e => setTren(e.target.value)}
            placeholder="38318"
            onKeyDown={e => e.key === 'Enter' && consultar()}
            style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, width: 120 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Fecha</label>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }} />
        </div>
        <button onClick={consultar} disabled={loading || !tren}
          style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#1a1a1a', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, opacity: loading || !tren ? 0.6 : 1 }}>
          {loading ? 'Consultando...' : 'Consultar'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fcebeb', border: '1px solid #e24b4a', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#a32d2d' }}>
          {error}
        </div>
      )}

      {resultado && (
        <div>
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e5e5', padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Tren</div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>{resultado.tren}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Fecha</div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{new Date(resultado.fecha).toLocaleDateString('es-ES')}</div>
              </div>
              {resultado.locomotora && (
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Locomotora</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>🚂 {resultado.locomotora}</div>
                </div>
              )}
              {resultado.vehiculos > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Vehículos</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{resultado.vehiculos}</div>
                </div>
              )}
              {resultado.toneladas > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Toneladas</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{resultado.toneladas} t</div>
                </div>
              )}
              {resultado.ltt > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>LTT</div>
                  <div style={{ fontSize: 16, fontWeight: 500 }}>{resultado.ltt} m</div>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {resultado.paradas.length > 0 ? (
            <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: 12, border: '1px solid #e5e5e5', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8f8f6' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 500, color: '#555' }}>Estación</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 500, color: '#555' }}>Hora real</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 500, color: '#555' }}>Situación</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 500, color: '#555' }}>Parada</th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.paradas.map((p, i) => {
                    const haPasado = !!p.tipo;
                    const horaReal = p.salida_real || p.llegada_real;
                    const retraso = p.retraso_salida || p.retraso_llegada;

                    let paradaMin = null;
                    if (haPasado && p.llegada_real && p.salida_real && p.llegada_real !== p.salida_real) {
                      const toMin = (h) => {
                        const s = String(h).replace(/\.\d+/, '').padStart(4, '0');
                        return parseInt(s.slice(0,2)) * 60 + parseInt(s.slice(2));
                      };
                      const diff = toMin(p.salida_real) - toMin(p.llegada_real);
                      if (diff > 0) paradaMin = diff;
                    }

                    const getSituacion = (r) => {
                      if (!r && r !== '0') return 'Puntual';
                      const n = parseInt(r);
                      if (n === 0) return 'Puntual';
                      if (n < 0) return `${Math.abs(n)} min adelanto`;
                      if (n <= 60) return `${n} min retraso`;
                      return `+${n} min retraso`;
                    };

                    const getColor = (r) => {
                      if (!r && r !== '0') return '#16a34a';
                      const n = parseInt(r);
                      if (n <= 0) return '#16a34a';
                      if (n <= 30) return '#854f0b';
                      return '#a32d2d';
                    };

                    const horaTeo = p.salida_teo || p.llegada_teo;

                    return (
                      <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 16px', fontWeight: 500, color: haPasado ? '#1a1a1a' : '#888' }}>{p.estacion}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                          {haPasado ? (
                            <span style={{ fontWeight: 500, color: '#1a1a1a' }}>{formatHora(horaReal)}</span>
                          ) : horaReal ? (
                            <span style={{ color: '#888', fontSize: 12, fontStyle: 'italic' }}>{formatHora(horaReal)} <span style={{ fontSize: 10, color: '#aaa' }}>est.</span></span>
                          ) : (
                            <span style={{ color: '#bbb', fontSize: 12, fontStyle: 'italic' }}>{formatHora(horaTeo)} <span style={{ fontSize: 10, color: '#ccc' }}>teo.</span></span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                          {(haPasado || horaReal) && retraso && (
                            <span style={{ color: haPasado ? getColor(retraso) : '#888', fontWeight: haPasado ? 500 : 400, fontSize: 12 }}>
                              {getSituacion(retraso)}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                          {paradaMin && (
                            <span style={{ background: '#faeeda', color: '#854f0b', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 500 }}>
                              {paradaMin} min parada
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e5e5', padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
              No hay datos de paradas para este tren en la fecha seleccionada
            </div>
          )}
          {resultado.paradas.length > 0 && (
            <div style={{ width: 420, flexShrink: 0, position: 'sticky', top: 16 }}>
              <MapaTren paradas={resultado.paradas} />
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
